import React, { useState } from 'react';
import { X, AlertCircle, ArrowRight, Info, CheckCircle2 } from 'lucide-react';
import '../styles/ParseFlowModals.css';
import { formatDate } from '../utils/mockData';

export const ConfirmParseModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="pf-overlay">
      <div className="pf-modal pf-confirm-modal">
        <div className="pf-icon-container">
          <div className="pf-orange-circle">!</div>
        </div>
        <h2 className="pf-title">Are you sure?</h2>
        <p className="pf-desc">Do you want to parse this document ?</p>
        <div className="pf-actions">
          <button className="pf-btn pf-btn-no" onClick={onCancel}>No</button>
          <button className="pf-btn pf-btn-yes" onClick={onConfirm}>Yes</button>
        </div>
      </div>
    </div>
  );
};

export const ParsingLoadingModal = ({ isOpen }) => {
  if (!isOpen) return null;

  return (
    <div className="pf-overlay">
      <div className="pf-modal pf-loading-modal">
        <h2 className="pf-loading-title">Parsing the details.</h2>
        <div className="pf-spinner-container">
          <div className="pf-spinner"></div>
          <div className="pf-doc-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" fill="#3b82f6"/>
              <path d="M14 2V8H20" fill="#bfdbfe"/>
              <rect x="8" y="13" width="8" height="2" rx="1" fill="white"/>
              <rect x="8" y="17" width="6" height="2" rx="1" fill="white"/>
              <circle cx="9.5" cy="8.5" r="1.5" fill="#f472b6"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export const DuplicateResumeModal = ({ isOpen, duplicateCandidate, currentView, onProceed, onCancel, onCheckInCandidate }) => {
  const [selectedAction, setSelectedAction] = useState('overwrite');

  if (!isOpen || !duplicateCandidate) return null;

  const candId = duplicateCandidate.CandidateId || duplicateCandidate.candidate_id;
  const candCode = duplicateCandidate.CandidateCode || duplicateCandidate.candidate_code || (candId ? `CAN-${String(candId).padStart(6, '0')}` : '');
  const candidateName = `${duplicateCandidate.FirstName || duplicateCandidate.first_name || ''} ${duplicateCandidate.LastName || duplicateCandidate.last_name || ''}`.trim() || 'Candidate';
  const isBenchCandidate = Boolean(
    duplicateCandidate.IsBench === true ||
    duplicateCandidate.is_bench === true ||
    duplicateCandidate.IsBench === 1 ||
    duplicateCandidate.is_bench === 1 ||
    duplicateCandidate.IsBench === 'true' ||
    duplicateCandidate.is_bench === 'true'
  );

  const email = duplicateCandidate.Contact?.Email || duplicateCandidate.Email || duplicateCandidate.email || '';
  const jobTitle = duplicateCandidate.JobTitle || duplicateCandidate.job_title || '';
  const isParsingOnBench = currentView === 'bench';

  const handleCheckIn = () => {
    if (onCheckInCandidate) {
      onCheckInCandidate(duplicateCandidate);
    } else {
      const targetView = isBenchCandidate ? 'bench' : 'sourcing';
      window.location.href = `/resume-dashboard?view=${targetView}&search=${encodeURIComponent(candidateName)}`;
    }
  };

  return (
    <div className="pf-overlay">
      <div className="pf-modal pf-duplicate-modal">
        <div className="pf-dup-header">
          <h2 className="pf-dup-title">Duplicate Resume</h2>
          <button className="pf-close-icon" onClick={onCancel}><X size={18} /></button>
        </div>

        <div className="pf-dup-body">
          <div className="pf-warning-bar">
            <AlertCircle size={16} color="#eab308" />
            <span>Candidate already exists with matching Email, Phone, or LinkedIn URL</span>
          </div>

          {/* Context Notice Banner when candidate is in Sourcing and user is parsing on Bench */}
          {!isBenchCandidate ? (
            <div className="pf-pool-notice-banner sourcing">
              <div className="pf-pool-notice-header">
                <span className="pf-pool-notice-badge sourcing">
                  📁 SOURCING CANDIDATE
                </span>
                <span className="pf-not-on-bench-tag">Not in Bench</span>
              </div>
              <div className="pf-pool-notice-desc">
                This candidate is currently located in <strong>Sourcing</strong>. {isParsingOnBench ? 'Proceeding will update their profile and add them to Bench Candidates.' : ''}
              </div>
            </div>
          ) : (
            <div className="pf-pool-notice-banner bench">
              <div className="pf-pool-notice-header">
                <span className="pf-pool-notice-badge bench">
                  🏢 BENCH CANDIDATE
                </span>
                <span className="pf-on-bench-tag">Already in Bench</span>
              </div>
              <div className="pf-pool-notice-desc">
                This candidate is already active in <strong>Bench Candidates</strong>.
              </div>
            </div>
          )}

          <div className="pf-dup-details">
            <div className="pf-detail-row">
              <span className="pf-detail-label">Candidate Name:</span>
              <div className="pf-detail-value pf-candidate-name-col">
                <span className="pf-name-plain">
                  {candidateName}
                </span>
                {candId && (
                  <button
                    type="button"
                    className="pf-check-sourcing-btn"
                    onClick={handleCheckIn}
                  >
                    <ArrowRight size={12} />
                    <span>Check in {isBenchCandidate ? 'Bench' : 'Sourcing'}</span>
                  </button>
                )}
              </div>
            </div>

            {candCode && (
              <div className="pf-detail-row">
                <span className="pf-detail-label">Candidate ID:</span>
                <span className="pf-detail-value pf-code-badge">{candCode}</span>
              </div>
            )}

            {email && (
              <div className="pf-detail-row">
                <span className="pf-detail-label">Email:</span>
                <span className="pf-detail-value">{email}</span>
              </div>
            )}

            {jobTitle && (
              <div className="pf-detail-row">
                <span className="pf-detail-label">Job Title:</span>
                <span className="pf-detail-value">{jobTitle}</span>
              </div>
            )}

            <div className="pf-detail-row">
              <span className="pf-detail-label">Status:</span>
              <span className="pf-detail-value">{duplicateCandidate.CandidateStatus || duplicateCandidate.status || 'Available'}</span>
            </div>

            <div className="pf-detail-row">
              <span className="pf-detail-label">Created Date:</span>
              <span className="pf-detail-value">{formatDate(duplicateCandidate.CreatedDt || duplicateCandidate.created_on)}</span>
            </div>
          </div>

          <div className="pf-dup-options">
            <label className="pf-radio-label">
              <input type="radio" name="dupAction" value="overwrite" 
                     checked={selectedAction === 'overwrite'} 
                     onChange={() => setSelectedAction('overwrite')} />
              <span>
                {isParsingOnBench && !isBenchCandidate 
                  ? 'Overwrite candidate profile & add to Bench' 
                  : 'Overwrite the candidate profile'}
              </span>
            </label>
            <label className="pf-radio-label">
              <input type="radio" name="dupAction" value="append" 
                     checked={selectedAction === 'append'} 
                     onChange={() => setSelectedAction('append')} />
              <span>
                {isParsingOnBench && !isBenchCandidate 
                  ? 'Append candidate profile & add to Bench' 
                  : 'Append the candidate profile'}
              </span>
            </label>
            <label className="pf-radio-label">
              <input type="radio" name="dupAction" value="add_resume" 
                     checked={selectedAction === 'add_resume'} 
                     onChange={() => setSelectedAction('add_resume')} />
              <span>
                {isParsingOnBench && !isBenchCandidate 
                  ? "Add resume and don't update candidate (add to Bench)" 
                  : "Add resume and don't update candidate"}
              </span>
            </label>
          </div>
        </div>

        <div className="pf-dup-footer">
          <button className="pf-btn pf-btn-no" onClick={onCancel}>Cancel</button>
          <button className="pf-btn pf-btn-yes" onClick={() => onProceed(selectedAction)}>Proceed</button>
        </div>
      </div>
    </div>
  );
};
